<template>
  <div class="pagination">
    <button 
      class="page-btn prev" 
      @click="handlePrevPage"
      :disabled="currentPage === 1"
    >
      上一页
    </button>
    <div class="page-numbers">
      <span 
        v-for="page in pageNumbers" 
        :key="page"
        class="page-number"
        :class="{ active: currentPage === page }"
        @click="handlePageChange(page)"
      >
        {{ page }}
      </span>
    </div>
    <button 
      class="page-btn next" 
      @click="handleNextPage"
      :disabled="currentPage === totalPages"
    >
      下一页
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  total: number;           // 总记录数
  pageSize?: number;       // 每页显示数量，默认 10
  currentPage: number;     // 当前页码
}

const props = withDefaults(defineProps<Props>(), {
  pageSize: 6
});

const emit = defineEmits<{
  change: [page: number];
}>();

// 总页数
const totalPages = computed(() => {
  return Math.ceil(props.total / props.pageSize);
});

// 上一页
const handlePrevPage = () => {
  if (props.currentPage > 1) {
    emit('change', props.currentPage - 1);
  }
};

// 下一页
const handleNextPage = () => {
  if (props.currentPage < totalPages.value) {
    emit('change', props.currentPage + 1);
  }
};

// 跳转到指定页
const handlePageChange = (page: number) => {
  emit('change', page);
};

// 生成页码列表（最多显示 5 个页码）
const pageNumbers = computed(() => {
  const pages: number[] = [];
  const total = totalPages.value;
  let start = Math.max(1, props.currentPage - 2);
  const end = Math.min(total, start + 4);
  
  // 调整起始位置，确保始终显示 5 个页码（如果总页数足够）
  if (end - start < 4 && total >= 5) {
    start = Math.max(1, end - 4);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  
  return pages;
});
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
}

.page-btn {
  padding: 10px 25px;
  border: 2px solid #1a5fb4;
  background: white;
  color: #1a5fb4;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s;
}

.page-btn:hover:not(:disabled) {
  background: #1a5fb4;
  color: white;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-numbers {
  display: flex;
  gap: 10px;
}

.page-number {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;
  color: #666;
}

.page-number:hover,
.page-number.active {
  background: #1a5fb4;
  color: white;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .pagination {
    flex-direction: column;
    gap: 15px;
  }
  
  .page-btn {
    padding: 8px 20px;
    font-size: 0.9rem;
    width: 100%;
    max-width: 200px;
  }
  
  .page-numbers {
    gap: 8px;
  }
  
  .page-number {
    width: 36px;
    height: 36px;
    font-size: 0.9rem;
  }
}

@media (max-width: 480px) {
  .page-btn {
    padding: 8px 15px;
    font-size: 0.85rem;
  }
  
  .page-number {
    width: 32px;
    height: 32px;
    font-size: 0.85rem;
  }
}
</style>